import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon as fa_icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import FormButton from "../form-components/FormButton.js";
import PropTypes from 'prop-types';

const Modal = ({
	show,
	close,
	title,
	description,
	warningMessage,
	cancelButton = "Cancel",
	confirmButton,
	icon,
	alertIcon,
	confirmAction,
	modal = true,
	size = "md", // sm, md, lg
	className = "",
	children,
}) => {
	// Handle keyboard events: Escape cancels, Enter triggers the confirm action (when one exists).
	// Enter is ignored inside a textarea / contenteditable so multi-line input still works.
	useEffect(() => {
		const handleKey = (e) => {
			if (!show) return;
			const tag = (e.target?.tagName || "").toLowerCase();
			if (e.key === "Escape") {
				close();
			} else if (
				e.key === "Enter" &&
				confirmAction &&
				confirmButton &&
				tag !== "textarea" &&
				!e.target?.isContentEditable
			) {
				e.preventDefault();
				confirmAction();
			}
		};

		document.addEventListener("keydown", handleKey);
		return () => document.removeEventListener("keydown", handleKey);
	}, [show, close, confirmAction, confirmButton]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (show) {
			document.body.style.overflow = "hidden";
		} else {
			document.body.style.overflow = "unset";
		}
		return () => {
			document.body.style.overflow = "unset";
		};
	}, [show]);

	const modalStyle = "bg-black bg-opacity-50 z-[60] fixed grid content-center place-content-center items-center top-0 left-0 right-0 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-[calc(100%-1rem)] max-h-full transition-opacity duration-300";
	const nonModalStyle = "bg-white bg-opacity-0 z-[60] fixed grid content-center place-content-center items-center top-0 left-0 right-0 p-4 overflow-y-auto transition-opacity duration-300";

	const sizeClasses = {
		sm: "w-96",
		md: "w-164",
		lg: "w-192"
	};

	const _icon = icon || fa_icon({ name: "triangle-exclamation", style: "solid" });

	return ReactDOM.createPortal(
		<>
			{show && (
				<div
					className={modal ? modalStyle : nonModalStyle}
					onClick={() => close()}
					role="dialog"
					aria-modal="true"
					aria-labelledby="modal-title"
					aria-describedby="modal-description"
				>
					<div
						className={`${sizeClasses[size]} bg-card relative border border-card-bd rounded-card shadow-float p-4 ${className}`}
						onClick={(e) => e.stopPropagation()}
					>
						<div className="flex flex-row">
							<div className="basis-1/6 text-center mt-2 mr-10 ml-2">
								<span className="fa-stack fa-xl fa-fw">
									<FontAwesomeIcon 
										icon={fa_icon({name: 'circle', style: 'solid'})} 
										className={alertIcon ? "text-danger-soft fa-stack-2x" : "text-primary-soft fa-stack-2x"}
									/>
									<FontAwesomeIcon 
										icon={_icon} 
										className={alertIcon ? "text-danger text-center fa-stack-1x fa-sm" : "text-primary text-center fa-stack-1x fa-sm"}
									/>
								</span> 
							</div>
							
							<div className="basis-5/6">
								<h2 id="modal-title" className="modal_header-title font-semibold text-lg mb-4">
									{title}
								</h2>

							<div id="modal-description" className="modal_header-title mb-4">
								{description}
							</div>

							{children}

							{warningMessage && (
								<div className="modal_header-title font-semibold text-danger">
									{warningMessage}
								</div>
							)}
							</div>
						</div>

						{/* Action area renders only when at least one button is set. Callers can pass
						    cancelButton="" (or null) to suppress the default Cancel button and render
						    their own actions inside `children` — useful when buttons need to align with
						    the content column instead of the icon-column-anchored pl-32 default. */}
						{(confirmButton || cancelButton) && (
							<div className="text-left pl-32 pt-8 pb-3">
								{confirmButton && (
									<FormButton
										title={confirmButton}
										style="primary"
										onClick={() => confirmAction()}
									/>
								)}
								{cancelButton && (
									<FormButton
										title={cancelButton}
										onClick={() => close()}
									/>
								)}
							</div>
						)}
					</div>
				</div>
			)}
		</>,
		document.body
	);
};

Modal.propTypes = {
	show: PropTypes.bool.isRequired,
	close: PropTypes.func.isRequired,
	title: PropTypes.string.isRequired,
	description: PropTypes.node,
	warningMessage: PropTypes.string,
	cancelButton: PropTypes.string,
	confirmButton: PropTypes.string,
	icon: PropTypes.object,
	alertIcon: PropTypes.bool,
	confirmAction: PropTypes.func,
	modal: PropTypes.bool,
	size: PropTypes.oneOf(['sm', 'md', 'lg']),
	className: PropTypes.string,
	children: PropTypes.node,
};

export default Modal;
